// Audit criteria of a Google Business Profile: turns the /gmb-audit response
// into the lists of valid criteria and issues shown in the "Overview" tab.
//
// The French texts are the ones of the former parseBusinessFromDBToValidFormat
// (src/utils/utils.js, kept word for word). The English texts are written for US businesses and
// leave out the unsourced statistics of the French version.

const CLAIMED_FR =
  "Revendiquez votre fiche Google my Business est la première étape pour votre SEO local. Si vous n'avez pas revendiqué votre GMB, vous ne pourrez pas optimiser votre fiche. Lorsque vous revendiquez votre fiche GMB, vous pouvez modifier vos informations. Si votre fiche n'est pas créé vous devez suivre la procédure pour valider votre établissement.";
const ADDRESS_FR =
  "Mettre une adresse si vous avez un établissement physique. Google favorise les fiches avec adresse car cela montre que vous êtes un vrai établissement et non un spam.";
const WEBSITE_FR =
  "Vous devez mettre la bonne adresse de votre site web. Ne pas utiliser votre page facebook ou linkedin dans cette section.";
const PHONE_FR =
  "Mettez votre numéro de téléphone principal et évitez qu'il soit changé.";
const REVIEWS_FR_END =
  "Vous devez améliorer votre e-réputation et votre seo local grâce aux avis. Avoir des avis vous permet d'avoir plus de visibilité sur Google. Les gens prennent de plus en plus une décision d'achat grâce aux avis. Une étude montre qu'avoir plus d'avis sur Google My Business peut augmenter vos conversions de 20%.";
const HOURS_FR =
  "Vos horaires d'ouvertures et fermetures doivent être visible pour informer vos clients de vos disponibilités. Ne vous trompez pas d'heure cela pourrait frustrer certains visiteurs.";
const QA_FR =
  "La FAQ permet aux internautes de poser des questions directement à l'entreprise. Elle est affichée au public donc il faut faire attention à ce que vos clients mettent dessus et bien y répondre. L'entreprise peut aussi créer ses propres questions et y répondre. La FAQ vous évite d'avoir des demandes inutiles par téléphone et améliore votre taux de clics.";
const DESCRIPTION_FR =
  "Ecrivez une petite description pour votre entreprise avec votre mot clé principal et votre zone géographique. N'hésitez pas à mettre en avant ce qui vous distingue des autres concurrents. Ne pas sur-optimiser votre texte, c'est à dire ne pas répéter plusieurs fois vos mots clés.";
const POSTS_FR =
  "Google posts vous permet d'avoir plus de visibilité et informer vos clients sur votre actualité. Ils vous donnent une meilleure visibilité et donne la possibilité d'attirer plus de clients dans votre établissement. Les Google posts sont visible sur les résultats de recherches sous votre fiche et améliore votre taux de clics.";
const RATING_FR =
  "Toujours avoir une note supérieure ou égale à 4 / 5. Des études montrent que avoir une bonne note générale améliorer votre taux de clics. Un établissement avec 5 étoiles recevra plus de clics qu'un établissement avec 2 étoiles.";
const PRODUCTS_FR =
  "Certains établissements peuvent montrer leurs produits ou services avec une description et les prix sur les résultats de recherches. Ils seront visibles sous la fiche de l'établissement. Lorsque vous avez renseigné vos produits/services, vous améliorez votre taux de clics et les conversions sur votre fiche.";

const CLAIMED_EN =
  "Claiming your Google Business Profile is the first step of local SEO. Until you claim it, you can't edit your information or optimize anything else. If your business doesn't have a profile yet, create one and complete Google's verification process.";
const ADDRESS_EN =
  "If customers can visit you, show your address. Google favors profiles with a real address because it signals a legitimate business, not spam.";
const WEBSITE_EN =
  "Link to your own website (ideally the page for this location). Don't use your Facebook or LinkedIn page as your website.";
const PHONE_EN =
  "Use your main phone number and keep it stable over time: frequent changes confuse customers and Google.";
const REVIEWS_EN =
  "Reviews drive both your local rankings and your reputation. More reviews mean more visibility on Google, and many people check reviews before choosing a business.";
const HOURS_EN =
  "Show your opening hours so customers know when they can reach you. Keep them accurate, including holiday hours: showing up to a closed store is a frustrating experience.";
const QA_EN =
  "Q&A lets people ask your business questions directly on Google. Answers are public, so monitor what gets posted and reply carefully. You can also add and answer your own common questions, which saves you phone calls and helps people choose you.";
const DESCRIPTION_EN =
  "Write a short business description that includes your main service and the area you serve, and highlight what sets you apart from competitors. Don't stuff it with keywords.";
const POSTS_EN =
  "Google posts keep customers up to date with your news, offers and events. They show up on your profile in search results and give people one more reason to visit or call.";
const RATING_EN =
  "Aim for an average rating of 4 stars or more. A business with a high rating looks more trustworthy and tends to get more clicks than one with a low rating.";
const PRODUCTS_EN =
  "Many businesses can list products or services, with descriptions and prices, right on their profile in search results. Filling them in helps people understand what you offer before they click or call.";

// Each criterion: `isValid(business)` and the texts in each language. A text
// is either `content` (same for valid and issue) or `validContent` /
// `issueContent`.
const CRITERIA = [
  {
    isValid: (b) => !!b.IsCompanyClaimed,
    fr: { valid: "Établissement revendiqué", issue: "Établissement non revendiqué", content: CLAIMED_FR },
    en: { valid: "Profile claimed", issue: "Profile not claimed", content: CLAIMED_EN },
  },
  {
    isValid: (b) => !!b.Address,
    fr: { valid: "Adresse renseignée", issue: "Adresse non renseignée", content: ADDRESS_FR },
    en: { valid: "Address added", issue: "Address missing", content: ADDRESS_EN },
  },
  {
    isValid: (b) => !!b.Website,
    fr: { valid: "Site web renseigné", issue: "Site web non renseigné", content: WEBSITE_FR },
    en: { valid: "Website added", issue: "Website missing", content: WEBSITE_EN },
  },
  {
    isValid: (b) => !!b.Phone,
    fr: { valid: "Téléphone renseigné", issue: "Téléphone non renseigné", content: PHONE_FR },
    en: { valid: "Phone number added", issue: "Phone number missing", content: PHONE_EN },
  },
  {
    isValid: (b) => !!b.HasReviews,
    fr: {
      valid: "Objectif premier avis",
      issue: "Objectif premier avis",
      validContent: `Vous avez obtenu votre premier avis, c'est super ! Ne vous arrêtez pas en si bon chemin ! ${REVIEWS_FR_END}`,
      issueContent: `Vous n'avez aucun avis sur votre fiche GMB. ${REVIEWS_FR_END}`,
    },
    en: {
      valid: "First review received",
      issue: "No reviews yet",
      validContent: `You got your first review, great start! Keep going. ${REVIEWS_EN}`,
      issueContent: `Your Google Business Profile has no reviews yet. ${REVIEWS_EN}`,
    },
  },
  {
    isValid: (b) => b.Nbreviews > 20,
    fr: {
      valid: "Vous avez plus de 20 avis",
      issue: "Vous n'avez pas assez d'avis",
      validContent:
        "Vous avez déjà plus de 20 avis, ce qui est un bon point. Cependant, nous vous conseillons de demander à vos clients plus d'avis ! Pour se faire, retrouvez dans la catégorie Avis quelques astuces.",
      issueContent:
        "Vous avez très peu d'avis, il va falloir corriger cela. Nous vous conseillons de demander à vos clients plus d'avis !",
    },
    en: {
      valid: "More than 20 reviews",
      issue: "Not enough reviews",
      validContent:
        "You already have more than 20 reviews, which is a good sign. Keep asking your customers for reviews: you'll find a few tips in the Reviews tab.",
      issueContent:
        "You have very few reviews. Make asking happy customers for a review part of your routine.",
    },
  },
  {
    isValid: (b) => !!b.IsBusinessHoursFilled,
    fr: { valid: "Horaires renseignés", issue: "Horaires non renseignés", content: HOURS_FR },
    en: { valid: "Business hours added", issue: "Business hours missing", content: HOURS_EN },
  },
  {
    isValid: (b) => !!b.IsQandAFilled,
    fr: { valid: "FAQ renseignée", issue: "FAQ non renseignée", content: QA_FR },
    en: { valid: "Q&A filled in", issue: "No Q&A", content: QA_EN },
  },
  {
    isValid: (b) => !!b.MerchandDescription,
    fr: { valid: "Description renseignée", issue: "Description non renseignée", content: DESCRIPTION_FR },
    en: { valid: "Description added", issue: "Description missing", content: DESCRIPTION_EN },
  },
  {
    isValid: (b) => !!b.HasSocialProfiles,
    fr: {
      valid: "Réseaux sociaux liés",
      issue: "Aucun réseau social lié",
      validContent:
        "Vos profils de réseaux sociaux sont liés à votre fiche Google Business Profile. Cela renforce la crédibilité de votre établissement et permet aux clients de vous retrouver facilement sur les différentes plateformes.",
      issueContent:
        "Liez vos profils de réseaux sociaux (Facebook, Instagram, LinkedIn, etc.) à votre fiche Google Business Profile. Cela renforce votre présence en ligne et permet aux clients de vous retrouver plus facilement.",
    },
    en: {
      valid: "Social profiles linked",
      issue: "No social profiles linked",
      validContent:
        "Your social media profiles are linked to your Google Business Profile. This builds trust and helps customers find you on the platforms they use.",
      issueContent:
        "Link your social media profiles (Facebook, Instagram, LinkedIn, etc.) to your Google Business Profile. It strengthens your online presence and helps customers find you more easily.",
    },
  },
  {
    isValid: (b) => !!b.HasPosts,
    fr: {
      valid: "Vous avez des Google Posts",
      issue: "Vous n'avez pas de Google Posts récents",
      validContent: `${POSTS_FR.slice(0, -1)}. Continuez à publier !`,
      issueContent: POSTS_FR,
    },
    en: {
      valid: "Recent Google posts",
      issue: "No recent Google posts",
      validContent: `${POSTS_EN} Keep posting!`,
      issueContent: POSTS_EN,
    },
  },
  {
    isValid: (b) => b.Rating >= 4,
    fr: { valid: "Votre note moyenne est bonne", issue: "Votre note moyenne n'est pas bonne", content: RATING_FR },
    en: { valid: "Good average rating", issue: "Average rating below 4 stars", content: RATING_EN },
  },
  {
    isValid: (b) => !!b.HasProducts,
    fr: { valid: "Produits renseignés", issue: "Aucun produit renseigné", content: PRODUCTS_FR },
    en: { valid: "Products or services listed", issue: "No products or services listed", content: PRODUCTS_EN },
  },
];

const DUPLICATES = {
  fr: {
    header: (n) => `${n} doublons potentiels détectés`,
    content:
      "Un doublon signifit qu'une fiche GMB ayant des informations similaires aux votres a été détecté. Cela peut hurter gravement votre e-réputation. Rendez-vous dans la section doublon pour plus de détails.",
  },
  en: {
    header: (n) => `${n} possible duplicate profiles found`,
    content:
      "A duplicate is another Google Business Profile with information similar to yours. Duplicates can split your reviews and hurt your visibility and reputation, so they should be merged or removed.",
  },
};

export const buildAuditCriteria = (business, lang = "fr") => {
  const valid = [];
  const issues = [];

  if (business.Duplicates) {
    const duplicates = JSON.parse(business.Duplicates);
    if (duplicates.length > 0) {
      issues.push({
        header: DUPLICATES[lang].header(duplicates.length),
        content: DUPLICATES[lang].content,
      });
    }
  }

  CRITERIA.forEach((criterion) => {
    const t = criterion[lang];
    if (criterion.isValid(business)) {
      valid.push({ header: t.valid, content: t.validContent || t.content });
    } else {
      issues.push({ header: t.issue, content: t.issueContent || t.content });
    }
  });

  return { business, valid, issues };
};
